import * as React from 'react'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import Divider from '@mui/material/Divider'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import NavLinkBase from 'design-system/NavLink'
import {
  CHAT,
  HOME,
  PATIENT_LIST,
  PROFESSIONALS,
  ESTABLISHMENTS,
  MEDICAL_EVENTS,
  CXRAY_ANALYSIS,
  ECG_ANALYSIS
} from 'navigation/CONSTANTS'
import {
  Typography,
  useTheme,
  styled,
  Theme,
  CSSObject,
  Tooltip
} from '@mui/material'
import { useAppSelector } from 'reduxStore/store'
import PermissionByRole, {
  PermissionByAdminRole,
  PermissionByModuleName,
  wrapWithPermissionsByModuleName
} from 'components/rules/PermissionByRole'
import { EstablishmentMenuMobile } from '../Header/components/EstablishmentMenu'
import MuiDrawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Cards } from 'pages/Home'
import { useLogo } from 'hooks'
import { useMemo } from 'react'

const drawerWidth = 250

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`
  }
})

const DrawerHeader = styled('div', {
  shouldForwardProp: prop => prop !== 'isOpen'
})<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: isOpen ? 'flex-end' : 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  minHeight: '56px !important'
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open'
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme)
      }
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme)
      }
    }
  ]
}))

const items = [
  PATIENT_LIST,
  PROFESSIONALS,
  ESTABLISHMENTS,
  MEDICAL_EVENTS,
  CXRAY_ANALYSIS,
  ECG_ANALYSIS
]

const menuItems = Cards(true)

export const chatItem = {
  link: CHAT,
  title: 'Chat',
  icon: (
    <span
      style={{ color: 'var(--secondarySoft-button-color)' }}
      className='material-symbols-outlined'
    >
      chat
    </span>
  ),

  iconBase: <span className='material-symbols-outlined'>chat</span>,
  rolesAllowed: [],
  superAdminOnly: false
}

interface IMiniDrawerProps {
  mobile?: boolean
}

/** SwipeableTemporaryDrawer component
 * This component renders a swipeable drawer for navigation.
 * It includes links to various sections of the application such as Patients, Colleagues, and X-Ray Process.
 * @returns JSX.Element
 */
export default function SwipeableTemporaryDrawer({
  mobile
}: Readonly<IMiniDrawerProps>) {
  const [isOpen, setIsOpen] = React.useState(false)
  const establishmentId = useAppSelector(
    rState => rState.establishment.data?.establishment_id
  )

  const theme = useTheme()
  const logo = useLogo()

  const textStyle = useMemo(
    () =>
      theme.palette.mode == 'dark'
        ? {
            color: theme.palette.text.primary
          }
        : {
            color: theme.palette.primarySoft.main
          },
    [theme.palette.mode]
  )

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event?.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return
      }

      setIsOpen(open)
    }

  if (!establishmentId) {
    return <></>
  }

  return (
    <div>
      <Drawer variant='permanent' open={isOpen}>
        <DrawerHeader isOpen={isOpen}>
          {isOpen && (
            <NavLinkBase to={HOME} onClick={toggleDrawer(false)} absolute>
              <Box
                component='img'
                src={logo}
                alt='sentinela logo'
                sx={{
                  height: 51
                }}
              />
            </NavLinkBase>
          )}

          {isOpen ? (
            <IconButton onClick={toggleDrawer(false)}>
              {theme.direction === 'rtl' ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )}
            </IconButton>
          ) : (
            <IconButton
              color='inherit'
              aria-label='open drawer'
              onClick={toggleDrawer(true)}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </DrawerHeader>
        <Divider />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            {mobile && (
              <>
                <ListItem
                  sx={{
                    mb: 1
                  }}
                >
                  <EstablishmentMenuMobile short={!isOpen} />
                </ListItem>
                <Divider />

              {wrapWithPermissionsByModuleName(
                (<NavLinkBase to={chatItem.link} absolute>
                  <ListItem disablePadding>
                    <ListItemButton>
                      <ListItemIcon>{chatItem.icon}</ListItemIcon>
                      {isOpen && (
                        <ListItemText>
                          <Typography variant='body1' color={textStyle.color}>
                            {chatItem.title}
                          </Typography>
                        </ListItemText>
                      )}
                    </ListItemButton>
                  </ListItem>
                </NavLinkBase>),
                true,
                ['chat']
              )}
                
              </>
            )}
            {items.map(link => {
              const card = menuItems[link]
              const rolesLength = card.rolesAllowed?.length ?? 0
              const modulesLength = card.modulesRequired?.length ?? 0
              const BaseComponent = (
                <NavLinkBase key={link} to={link} absolute>
                  <ListItem disablePadding>
                    <Tooltip
                      title={card.label}
                      placement='right'
                      disableHoverListener={isOpen}
                    >
                      <ListItemButton color={textStyle.color}>
                        <ListItemIcon>{card.icon}</ListItemIcon>
                        {isOpen && (
                          <ListItemText>
                            <Typography
                              variant='body1'
                              color={textStyle.color}
                              sx={{
                                textWrap: 'auto'
                              }}
                            >
                              {card.label}
                            </Typography>
                          </ListItemText>
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                </NavLinkBase>
              )
              if (card.superAdminOnly) {
                return (
                  <PermissionByAdminRole key={link}>
                    {BaseComponent}
                  </PermissionByAdminRole>
                )
              } else if (rolesLength > 0) {
                return (
                  <PermissionByRole
                    key={link}
                    rolesAllowed={card.rolesAllowed ?? []}
                  >
                    {BaseComponent}
                  </PermissionByRole>
                )
              } else if (modulesLength > 0) {
                return (
                  <PermissionByModuleName
                    key={link}
                    modulesRequired={card.modulesRequired ?? []}
                  >
                    {BaseComponent}
                  </PermissionByModuleName>
                )
              } else {
                return BaseComponent
              }
            })}
          </List>
        </Box>
      </Drawer>
    </div>
  )
}
